import { useEffect, useState } from "react";
import { ShieldCheck, Camera, Mic, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * First-use consent modal — required by the SonSpeak privacy framework.
 *
 * Shown the FIRST time a user tries to upload a photo or record audio for
 * a profile (or any other gated action). Once accepted, we POST to the
 * consent log so the same user/profile/feature combination never re-prompts
 * for that consent version. If we later increment the canonical version,
 * the user re-sees this modal automatically.
 *
 * Props:
 *   feature       — "photos" | "voice" (drives the wording + icon)
 *   profileId     — string, scopes consent to this profile
 *   childProfile  — boolean, swaps to the parent/guardian variant of the copy
 *   open          — boolean, parent controls visibility
 *   onAccept      — () => void, fires after the consent is logged
 *   onCancel      — () => void, fires if the user closes without accepting
 *
 * Usage idiom:
 *   const { needsConsent, accept } = useFirstUseConsent("photos", profileId, isChild);
 *   if (needsConsent) return <FirstUseConsent feature="photos" ... />;
 */
const COPY = {
  photos: {
    icon: Camera,
    title: "About using a real photo",
    body: (
      <>
        You can use a real picture for this tile. Photos are{" "}
        <strong>optional</strong> and can be removed anytime. Photos may
        include personal information, faces, locations, home details, school
        details, or other sensitive information.{" "}
        <strong>Only upload or take photos you have permission to use.</strong>
      </>
    ),
    childAddon: (
      <>
        As the parent or guardian, you are responsible for choosing photos
        for this child profile and confirming that you have permission to
        use them.
      </>
    ),
    confirm: "I understand — continue to photo",
  },
  voice: {
    icon: Mic,
    title: "About recording a familiar voice",
    body: (
      <>
        You can record a familiar voice for this tile. Recordings are{" "}
        <strong>optional</strong> and can be deleted anytime.{" "}
        <strong>Only record or upload voices you have permission to use.</strong>{" "}
        This feature stores simple recorded audio and{" "}
        <strong>does not create an AI voice clone</strong>.
      </>
    ),
    childAddon: (
      <>
        As the parent or guardian, you are responsible for choosing whether
        recorded voices may be used for this child profile.
      </>
    ),
    confirm: "I understand — continue to record",
  },
};

const FEATURE_TO_TYPE = {
  photos: { adult: "photo_upload_first_use", child: "child_profile_photo_first_use" },
  voice: { adult: "voice_recording_first_use", child: "child_profile_voice_first_use" },
};

export default function FirstUseConsent({
  feature,
  profileId,
  childProfile = false,
  open,
  onAccept,
  onCancel,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;
  const meta = COPY[feature];
  if (!meta) return null;
  const Icon = meta.icon;
  const type = FEATURE_TO_TYPE[feature][childProfile ? "child" : "adult"];

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/consent/record", {
        consent_type: type,
        profile_id: profileId,
        meta: { feature, child_profile: childProfile },
      });
      onAccept?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save your consent.");
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      data-testid={`first-use-consent-${feature}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="brutal-card bg-white p-6 w-full max-w-lg">
        <div className="flex items-start gap-3 mb-4">
          <span className="shrink-0 w-12 h-12 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--coral)] text-white flex items-center justify-center">
            <Icon size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)] font-bold flex items-center gap-1">
              <ShieldCheck size={12} className="text-[color:var(--coral)]" />
              Before you continue
            </div>
            <h2 className="display text-2xl font-extrabold leading-tight mt-0.5">
              {meta.title}
            </h2>
          </div>
          <button
            type="button"
            className="pill-btn"
            onClick={onCancel}
            disabled={busy}
            aria-label="Cancel"
            data-testid={`first-use-consent-${feature}-cancel`}
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-3 text-base leading-relaxed text-[color:var(--ink)]">
          <p>{meta.body}</p>
          {childProfile && (
            <p className="border-l-[4px] border-[color:var(--coral)] pl-3 italic">
              {meta.childAddon}
            </p>
          )}
          <p className="text-sm text-[color:var(--ink-soft)]">
            We log your acceptance with a timestamp. You can review or revoke
            it any time on the privacy dashboard.
          </p>
        </div>

        {error && (
          <div
            className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 mt-4 text-sm"
            data-testid={`first-use-consent-${feature}-error`}
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 mt-6">
          <button
            type="button"
            className="pill-btn flex-1 justify-center"
            onClick={onCancel}
            disabled={busy}
            data-testid={`first-use-consent-${feature}-decline`}
          >
            Not now
          </button>
          <button
            type="button"
            className="pill-btn primary flex-1 justify-center"
            onClick={accept}
            disabled={busy}
            data-testid={`first-use-consent-${feature}-accept`}
          >
            {busy ? "Saving…" : meta.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook that resolves whether a profile/feature combo still needs first-use
 * consent. Returns:
 *   - status: "loading" | "needed" | "ok"
 *   - markAccepted(): optimistic flip after the modal accepts
 */
export function useFirstUseConsent({ feature, profileId, childProfile = false, enabled = true }) {
  const [status, setStatus] = useState("loading");
  const type = FEATURE_TO_TYPE[feature]?.[childProfile ? "child" : "adult"];

  useEffect(() => {
    if (!enabled || !type || !profileId) {
      setStatus("ok");
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/consent/check?consent_type=${encodeURIComponent(type)}&profile_id=${encodeURIComponent(profileId)}`,
        );
        if (alive) setStatus(data?.accepted ? "ok" : "needed");
      } catch (err) {
        // Fail open — don't block the user, but log so we can debug.
        console.warn("FirstUseConsent: check failed", err?.response?.status || err?.message);
        if (alive) setStatus("ok");
      }
    })();
    return () => { alive = false; };
  }, [type, profileId, enabled]);

  return {
    status,
    markAccepted: () => setStatus("ok"),
  };
}
